import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta, useJsonLd } from "../../hooks/useSeo";
import { Breadcrumbs } from "../../components/base/NewsCard";

const SUBMIT_URL = "https://readdy.ai/api/form/dagn2chtto0ceqqni0h0";

const faqs = [
  {
    q: "每日爆料网的内容是从哪里来的？",
    a: "我们的内容主要来自日常的行业观察、公开信息的梳理，以及经读者与知情人士提供的线索。所有线索都会经过人工核实与多信源交叉比对后，才决定是否跟进报道。",
  },
  {
    q: "报道中的信息如何保证准确？",
    a: "我们坚持「先核实、后发布」的原则。涉及关键事实会尽量获取多方说法，对暂时无法确认的信息会明确标注或暂缓发布，避免因求快而传播错误内容。",
  },
  {
    q: "如何看待爆料与隐私的边界？",
    a: "娱乐资讯需要关注度，但我们同样尊重当事人的合法权益。涉及个人隐私、未成年人及可能引发伤害的信息，我们会格外审慎，宁可少报道，也不越界。",
  },
  {
    q: "我提供线索后，会得到回应吗？",
    a: "会的。对有价值的独家线索，编辑会优先跟进并在核实后回信告知进展。出于安全与客观考虑，我们不承诺所有线索都会被公开发布，请理解。",
  },
  {
    q: "可以转载或引用你们的内容吗？",
    a: "欢迎在注明来源「每日爆料网」的前提下进行合理引用。若需较大篇幅转载或商用，建议先通过联系页面与我们沟通授权，未经授权请勿整体搬运。",
  },
  {
    q: "发现内容有误，如何反馈？",
    a: "如果你认为某篇文章存在事实错误，欢迎通过下方联系/投稿表单提交具体指正。一经核实，我们会第一时间更正并在此说明，感谢你的监督。",
  },
];

export default function Faq() {
  const [open, setOpen] = useState<boolean[]>(faqs.map(() => true));
  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");
  const [msg, setMsg] = useState("");
  const [textLen, setTextLen] = useState(0);

  const toggle = (i: number) => setOpen((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  usePageMeta({
    title: "常见问题与投稿联系 - 每日爆料网",
    description:
      "每日爆料网常见问题解答，以及独家线索投稿与联系渠道。了解我们的内容来源、报道原则与转载授权方式。",
  });

  useJsonLd("faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = ((fd.get("contact_alt") as string) || "").trim();
    setStatus("idle");
    setMsg("");
    const show = (s: typeof status, text: string, reset?: boolean) => {
      setStatus(s);
      setMsg(text);
      if (reset) form.reset();
      if (reset) setTextLen(0);
    };
    if (honeypot) {
      show("ok", "已收到，感谢你的来信！我们会尽快核实并回复。", true);
      return;
    }
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== "contact_alt" && value) payload.append(key, String(value));
    });
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore */
      }
      const serverMsg =
        (parsed?.meta as Record<string, unknown>)?.message ||
        (parsed as Record<string, unknown>)?.message ||
        (parsed?.meta as Record<string, unknown>)?.detail ||
        "";
      if (res.ok && (parsed as Record<string, unknown>)?.code === "OK") {
        show("ok", "已收到，感谢你的来信！我们会尽快核实并回复。", true);
      } else {
        show("err", typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      show("err", "网络异常，请稍后再试。");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />

      <header className="rounded-2xl bg-primary-600 p-8 text-background-50 md:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium">
          <i className="ri-question-line text-accent-300" /> 帮助中心
        </span>
        <h1 className="mt-4 font-serif text-3xl font-black leading-snug md:text-4xl">
          常见问题，一页说清
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100/90">
          关于内容来源、报道原则、线索投稿与转载授权，这里都有答案。没找到你想问的？直接用下方表单告诉我们。
        </p>
      </header>

      {/* FAQ 手风琴 */}
      <section className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="font-serif text-2xl font-black text-foreground-950">你可能想问</h2>
          <div className="mt-5 space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
                <button
                  onClick={() => toggle(i)}
                  aria-expanded={open[i]}
                  className="flex w-full cursor-pointer items-center justify-between gap-3 px-5 py-4 text-left"
                >
                  <span className="font-serif text-base font-bold text-foreground-950 md:text-lg">{f.q}</span>
                  <i
                    className={`${open[i] ? "ri-subtract-line bg-primary-100 text-primary-600" : "ri-add-line bg-background-100 text-foreground-600"} flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm transition-colors`}
                  />
                </button>
                <div className={`grid transition-all duration-300 ${open[i] ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <p className="border-t border-background-200 px-5 py-4 text-[15px] leading-relaxed text-foreground-700">
                      {f.a}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 投稿与联系 */}
        <div className="space-y-6">
          <div id="contact" className="scroll-mt-24 rounded-xl border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 font-serif text-lg font-black text-foreground-950">
              <i className="ri-mail-open-line text-primary-500" /> 直接联系我们
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              投稿、反馈、授权与合作，都欢迎通过右侧表单或邮箱来信。
            </p>
            <a
              href="mailto:contact@meiribaoliao.example"
              className="mt-4 flex items-center gap-2 rounded-lg bg-secondary-100 px-4 py-3 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              <i className="ri-mail-line text-lg" /> contact@meiribaoliao.example
            </a>
            <ul className="mt-4 space-y-2 text-sm text-foreground-700">
              <li className="flex items-center gap-2"><i className="ri-time-line text-foreground-500" /> 来信一般 1–3 个工作日内回复</li>
              <li className="flex items-center gap-2"><i className="ri-shield-check-line text-foreground-500" /> 投稿信息将严格保密</li>
            </ul>
          </div>

          <div id="tougao" className="scroll-mt-24 rounded-xl border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 font-serif text-lg font-black text-foreground-950">
              <i className="ri-megaphone-line text-accent-500" /> 线索投稿 / 留言
            </h2>
            <p className="mt-2 text-sm text-foreground-600">带 * 为必填项，内容请控制在 500 字以内。</p>
            <form onSubmit={onSubmit} data-readdy-form className="mt-4 space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="f-name" className="mb-1 block text-sm text-foreground-800">称呼 *</label>
                  <input
                    id="f-name"
                    name="name"
                    required
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="f-email" className="mb-1 block text-sm text-foreground-800">你的邮箱 *</label>
                  <input
                    id="f-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于回复你"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-400"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="f-topic" className="mb-1 block text-sm text-foreground-800">来信类型</label>
                <select
                  id="f-topic"
                  name="topic"
                  defaultValue="独家线索"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-400"
                >
                  {["独家线索", "选题建议", "事实反馈", "授权合作", "其他"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="f-message" className="mb-1 block text-sm text-foreground-800">详细内容 *</label>
                <textarea
                  id="f-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  onChange={(e) => setTextLen(e.target.value.length)}
                  placeholder="请尽量描述事件时间、涉及对象与你的线索或问题…"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-400"
                />
                <p className="mt-1 text-right text-xs text-foreground-500">{textLen} / 500</p>
              </div>
              <input
                type="text"
                name="contact_alt"
                aria-hidden="true"
                tabIndex={-1}
                autoComplete="off"
                readOnly
                className="field-guard"
              />
              <button
                type="submit"
                className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                提交投稿 / 留言
              </button>
              {status === "ok" && (
                <p className="rounded-md bg-accent-100 px-3 py-2 text-sm leading-relaxed text-accent-900">{msg}</p>
              )}
              {status === "err" && (
                <p className="rounded-md bg-primary-100 px-3 py-2 text-sm leading-relaxed text-primary-700">{msg}</p>
              )}
              <p className="text-xs leading-relaxed text-foreground-500">
                提交即表示你同意我们为核实与回复处理所填写的信息。
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* 其他帮助 */}
      <section className="mt-12 flex flex-col items-center justify-between gap-4 rounded-xl bg-secondary-100 p-6 sm:flex-row">
        <div>
          <h2 className="font-serif text-lg font-black text-secondary-900">还想了解每日爆料网的更多信息？</h2>
          <p className="mt-1 text-sm text-secondary-700">去看看品牌介绍与内容定位吧。</p>
        </div>
        <Link
          to="/about"
          className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-secondary-700 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-800"
        >
          关于我们 <i className="ri-arrow-right-line" />
        </Link>
      </section>
    </div>
  );
}